// Comic text pops (§11): "BONK!", "CLANG!", "YEET!"… drawn once into a CanvasTexture atlas in
// Lilita One with a dark plum outline and a sticker drop shadow, then shown as instanced quads in a
// screen-space overlay that is composited after post-processing, so pops stay crisp and readable on
// every tier (no tilt-shift blur, no bloom, no grade). A pop scale-springs 0 → 1.25 → 1.0 over
// 220 ms, drifts up 0.6 m and fades after 700 ms. The same overlay draws item icon pops and the
// GAME! stamp.
import {
  CanvasTexture, DataTexture, DoubleSide, DynamicDrawUsage, InstancedBufferAttribute, InstancedBufferGeometry,
  LinearFilter, LinearMipmapLinearFilter, Mesh, OrthographicCamera, PlaneGeometry, RGBAFormat, Scene, ShaderMaterial,
  SRGBColorSpace, Vector3, type PerspectiveCamera, type Texture,
} from 'three';
import { ItemKind } from '@shared/ids';

export const Word = {
  BONK: 0, CLANG: 1, YOINK: 2, YEET: 3, BREAK: 4, CLUTCH: 5, CRUNCH: 6, OOPS: 7, WHOOPS: 8, KABOOM: 9,
  POW: 10, THWOP: 11, DOUBLE_BONK: 12, PLINK: 13, PLUS100: 14, HONK: 15, CLUTCH_BALLOON: 16, GAME: 17,
  SPLAT: 18, BIG: 19, POP: 20, HOT: 21,
  ICON_BANANA: 22, ICON_BOMB: 23, ICON_GLOVE: 24, ICON_BALLOON: 25, ICON_PLUNGER: 26, ICON_SHAKE: 27,
} as const;
export type Word = (typeof Word)[keyof typeof Word];

/** Text, gradient (top → bottom) and outline style per word. */
interface WordStyle { text: string; top: string; bottom: string; size?: number; frame?: 'balloon' }
const INK = '#2A1A3A';
const STYLES: Readonly<Partial<Record<Word, WordStyle>>> = {
  [Word.BONK]: { text: 'BONK!', top: '#FFF27A', bottom: '#FFB22E' },
  [Word.CLANG]: { text: 'CLANG!', top: '#FFFFFF', bottom: '#8FE3FF' },
  [Word.YOINK]: { text: 'YOINK!', top: '#FFB8E0', bottom: '#FF4F9A' },
  [Word.YEET]: { text: 'YEET!', top: '#FFD27A', bottom: '#FF7A2E' },
  [Word.BREAK]: { text: 'BREAK!', top: '#C8FFE4', bottom: '#3DDC97' },
  [Word.CLUTCH]: { text: 'CLUTCH!', top: '#FFF6B8', bottom: '#FFC21A' },
  [Word.CRUNCH]: { text: 'CRUNCH!', top: '#FFB0A0', bottom: '#FF4D4D' },
  [Word.OOPS]: { text: 'OOPS!', top: '#E6DDFF', bottom: '#9B6BFF' },
  [Word.WHOOPS]: { text: 'WHOOPS!', top: '#FFF7A8', bottom: '#F5D020' },
  [Word.KABOOM]: { text: 'KABOOM!', top: '#FFE37A', bottom: '#FF4A1C', size: 1.15 },
  [Word.POW]: { text: 'POW!', top: '#FFFFFF', bottom: '#FF4D4D', size: 1.2 },
  [Word.THWOP]: { text: 'THWOP!', top: '#FFC2E8', bottom: '#E8358E' },
  [Word.DOUBLE_BONK]: { text: 'DOUBLE BONK!', top: '#FFF27A', bottom: '#FF4F9A' },
  [Word.PLINK]: { text: 'PLINK!', top: '#FFFFFF', bottom: '#AFC3D6' },
  [Word.PLUS100]: { text: '+100', top: '#FFFFFF', bottom: '#2FF3FF' },
  [Word.HONK]: { text: 'HONK!', top: '#FFFFFF', bottom: '#FFD24A', size: 0.8 },
  [Word.CLUTCH_BALLOON]: { text: 'CLUTCH!', top: '#FFF6B8', bottom: '#FFC21A', frame: 'balloon' },
  [Word.GAME]: { text: 'GAME!', top: '#FFF6B8', bottom: '#FFB21A', size: 2 },
  [Word.SPLAT]: { text: 'SPLAT!', top: '#FFF7A8', bottom: '#E8C620' },
  [Word.BIG]: { text: 'BIG!', top: '#E6DDFF', bottom: '#4FD1C5' },
  [Word.POP]: { text: 'POP!', top: '#FFFFFF', bottom: '#FF7AC8' },
  [Word.HOT]: { text: 'HOT!', top: '#FFE37A', bottom: '#FF4A1C' },
};

export const ITEM_ICON: Readonly<Record<number, Word>> = {
  [ItemKind.BANANA]: Word.ICON_BANANA, [ItemKind.BOMB]: Word.ICON_BOMB, [ItemKind.GLOVE]: Word.ICON_GLOVE,
  [ItemKind.BALLOON]: Word.ICON_BALLOON, [ItemKind.PLUNGER]: Word.ICON_PLUNGER, [ItemKind.SHAKE]: Word.ICON_SHAKE,
};

const WORD_COUNT = 28;
const FONT_PX = 96;
const ICON_PX = 128;
const ATLAS_W = 2048;
const ATLAS_H = 1024;

/** Pixel rect of each word in the atlas (x, y, w, h) and its aspect. */
const rects = new Float32Array(WORD_COUNT * 4);

function drawBalloonFrame(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  const cx = x + w / 2;
  const cy = y + h * 0.46;
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(cx, cy, w * 0.48, h * 0.42, 0, 0, Math.PI * 2);
  ctx.fillStyle = '#FF7AC8';
  ctx.fill();
  ctx.lineWidth = 8;
  ctx.strokeStyle = INK;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - 12, cy + h * 0.41);
  ctx.lineTo(cx + 12, cy + h * 0.41);
  ctx.lineTo(cx, cy + h * 0.5);
  ctx.closePath();
  ctx.fillStyle = '#FF7AC8';
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(cx - w * 0.28, cy - h * 0.2, w * 0.07, h * 0.1, -0.5, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fill();
  ctx.restore();
}

function drawWord(ctx: CanvasRenderingContext2D, st: WordStyle, x: number, y: number): [number, number] {
  const px = FONT_PX * (st.size ?? 1);
  ctx.font = `${px}px "Lilita One", "Arial Rounded MT Bold", sans-serif`;
  const tw = Math.ceil(ctx.measureText(st.text).width);
  const stroke = px * 0.2;
  const shadow = px * 0.1;
  const padX = Math.ceil(stroke + px * 0.06);
  let w = tw + padX * 2;
  let h = Math.ceil(px * 1.12 + stroke + shadow);
  if (st.frame === 'balloon') {
    w = Math.ceil(w * 1.18);
    h = Math.ceil(h * 1.55);
    drawBalloonFrame(ctx, x, y, w, h);
  }
  const bx = x + w / 2;
  const by = y + (st.frame === 'balloon' ? h * 0.46 + px * 0.36 : stroke / 2 + px * 0.86);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.lineJoin = 'round';
  ctx.miterLimit = 2;
  // Sticker drop shadow.
  ctx.fillStyle = INK;
  ctx.strokeStyle = INK;
  ctx.lineWidth = stroke;
  ctx.strokeText(st.text, bx, by + shadow);
  ctx.fillText(st.text, bx, by + shadow);
  // Ink outline.
  ctx.strokeText(st.text, bx, by);
  // Glossy fill.
  const grad = ctx.createLinearGradient(0, by - px * 0.78, 0, by + px * 0.05);
  grad.addColorStop(0, st.top);
  grad.addColorStop(0.45, st.top);
  grad.addColorStop(0.55, st.bottom);
  grad.addColorStop(1, st.bottom);
  ctx.fillStyle = grad;
  ctx.fillText(st.text, bx, by);
  // A thin highlight line on the upper half (comic gloss).
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, by - px * 0.8, w, px * 0.3);
  ctx.clip();
  ctx.lineWidth = px * 0.035;
  ctx.strokeStyle = 'rgba(255,255,255,0.75)';
  ctx.strokeText(st.text, bx, by - px * 0.02);
  ctx.restore();
  return [w, h];
}

/** Item icons (§11.3 "item icon pops above the bean"): full-color sticker drawings. */
function drawIcon(ctx: CanvasRenderingContext2D, kind: number, x: number, y: number): void {
  const s = ICON_PX;
  const cx = x + s / 2;
  const cy = y + s / 2;
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  // Sticker disc.
  ctx.beginPath();
  ctx.arc(cx, cy + 4, s * 0.44, 0, Math.PI * 2);
  ctx.fillStyle = INK;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, s * 0.44, 0, Math.PI * 2);
  ctx.fillStyle = '#FFF6E9';
  ctx.fill();
  ctx.lineWidth = 7;
  ctx.strokeStyle = INK;
  ctx.stroke();
  ctx.lineWidth = 6;
  const ink = (): void => {
    ctx.strokeStyle = INK;
    ctx.stroke();
  };
  switch (kind) {
    case ItemKind.BANANA:
      ctx.beginPath();
      ctx.moveTo(cx - 34, cy - 10);
      ctx.quadraticCurveTo(cx - 20, cy + 40, cx + 34, cy + 12);
      ctx.quadraticCurveTo(cx + 4, cy + 18, cx - 22, cy - 18);
      ctx.closePath();
      ctx.fillStyle = '#FFD93C';
      ctx.fill();
      ink();
      ctx.beginPath();
      ctx.moveTo(cx - 22, cy - 18);
      ctx.lineTo(cx - 30, cy - 30);
      ink();
      break;
    case ItemKind.BOMB:
      ctx.beginPath();
      ctx.arc(cx - 4, cy + 8, 30, 0, Math.PI * 2);
      ctx.fillStyle = '#3A2A4A';
      ctx.fill();
      ink();
      ctx.beginPath();
      ctx.moveTo(cx + 14, cy - 16);
      ctx.quadraticCurveTo(cx + 26, cy - 34, cx + 36, cy - 30);
      ink();
      ctx.beginPath();
      ctx.arc(cx + 38, cy - 32, 7, 0, Math.PI * 2);
      ctx.fillStyle = '#FFC93C';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx - 14, cy - 2, 7, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.fill();
      break;
    case ItemKind.GLOVE:
      ctx.beginPath();
      ctx.roundRect(cx - 30, cy - 26, 52, 46, 20);
      ctx.fillStyle = '#FF4D4D';
      ctx.fill();
      ink();
      ctx.beginPath();
      ctx.roundRect(cx - 40, cy - 8, 20, 26, 9);
      ctx.fill();
      ink();
      ctx.beginPath();
      ctx.roundRect(cx - 24, cy + 18, 40, 16, 5);
      ctx.fillStyle = '#FFF6E9';
      ctx.fill();
      ink();
      break;
    case ItemKind.BALLOON:
      ctx.beginPath();
      ctx.ellipse(cx, cy - 8, 27, 32, 0, 0, Math.PI * 2);
      ctx.fillStyle = '#FF7AC8';
      ctx.fill();
      ink();
      ctx.beginPath();
      ctx.moveTo(cx, cy + 24);
      ctx.quadraticCurveTo(cx - 10, cy + 34, cx + 4, cy + 44);
      ink();
      ctx.beginPath();
      ctx.ellipse(cx - 10, cy - 20, 6, 9, -0.5, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fill();
      break;
    case ItemKind.PLUNGER:
      ctx.beginPath();
      ctx.rect(cx - 5, cy - 40, 10, 50);
      ctx.fillStyle = '#E0915A';
      ctx.fill();
      ink();
      ctx.beginPath();
      ctx.moveTo(cx - 30, cy + 34);
      ctx.quadraticCurveTo(cx - 30, cy + 4, cx, cy + 4);
      ctx.quadraticCurveTo(cx + 30, cy + 4, cx + 30, cy + 34);
      ctx.closePath();
      ctx.fillStyle = '#FF4D4D';
      ctx.fill();
      ink();
      break;
    default: // Big Shake: a milkshake cup with a straw.
      ctx.beginPath();
      ctx.moveTo(cx - 26, cy - 18);
      ctx.lineTo(cx + 26, cy - 18);
      ctx.lineTo(cx + 18, cy + 40);
      ctx.lineTo(cx - 18, cy + 40);
      ctx.closePath();
      ctx.fillStyle = '#4FD1C5';
      ctx.fill();
      ink();
      ctx.beginPath();
      ctx.arc(cx, cy - 18, 26, Math.PI, 0);
      ctx.fillStyle = '#FFB3C7';
      ctx.fill();
      ink();
      ctx.beginPath();
      ctx.moveTo(cx + 6, cy - 40);
      ctx.lineTo(cx + 20, cy - 56);
      ctx.lineWidth = 8;
      ink();
      break;
  }
  ctx.restore();
}

let atlasTex: Texture | null = null;

function placeholderTexture(): Texture {
  const t = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, RGBAFormat);
  t.needsUpdate = true;
  for (let i = 0; i < WORD_COUNT; i++) rects.set([0, 0, 1, 1], i * 4);
  return t;
}

function paint(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  let x = 4;
  let y = 4;
  let rowH = 0;
  const place = (w: number, h: number): [number, number] => {
    if (x + w > ATLAS_W - 4) {
      x = 4;
      y += rowH + 6;
      rowH = 0;
    }
    const at: [number, number] = [x, y];
    x += w + 6;
    rowH = Math.max(rowH, h);
    return at;
  };
  for (let id = 0; id < WORD_COUNT; id++) {
    const st = STYLES[id as Word];
    if (st) {
      // Measure first by drawing off-canvas, then draw in place.
      ctx.save();
      ctx.translate(-10000, -10000);
      const [w, h] = drawWord(ctx, st, 0, 0);
      ctx.restore();
      const [px, py] = place(w, h);
      drawWord(ctx, st, px, py);
      rects.set([px, py, w, h], id * 4);
    } else {
      const kind = id - Word.ICON_BANANA + ItemKind.BANANA;
      const [px, py] = place(ICON_PX, ICON_PX);
      drawIcon(ctx, kind, px, py);
      rects.set([px, py, ICON_PX, ICON_PX], id * 4);
    }
  }
}

/** The pop atlas (drawn once Lilita One is available; redrawn if the font arrives later). */
export function popAtlas(): Texture {
  if (atlasTex) return atlasTex;
  if (typeof document === 'undefined') {
    atlasTex = placeholderTexture();
    return atlasTex;
  }
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_W;
  canvas.height = ATLAS_H;
  paint(canvas);
  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 4;
  atlasTex = tex;
  const font = `${FONT_PX}px "Lilita One"`;
  if (document.fonts && !document.fonts.check(font)) {
    void document.fonts.load(font).then(() => {
      paint(canvas);
      tex.needsUpdate = true;
    }).catch(() => undefined);
  }
  return tex;
}

/** Atlas aspect (width / height) of a word, for layout. */
export function wordAspect(w: Word): number {
  return rects[w * 4 + 2] / Math.max(1, rects[w * 4 + 3]);
}

const POP_VERTEX = /* glsl */ `
attribute vec4 iRect;   // center x, center y (px), width, height (px)
attribute vec4 iUv;     // u0, v0, du, dv
attribute vec2 iRotA;   // rotation, alpha
varying vec2 vUv;
varying float vAlpha;
void main() {
  vec2 c = position.xy * iRect.zw;
  float cs = cos(iRotA.x);
  float sn = sin(iRotA.x);
  c = vec2(c.x * cs - c.y * sn, c.x * sn + c.y * cs);
  gl_Position = projectionMatrix * vec4(iRect.xy + c, 0.0, 1.0);
  vUv = vec2(iUv.x + uv.x * iUv.z, iUv.y + uv.y * iUv.w);
  vAlpha = iRotA.y;
}`;
const POP_FRAGMENT = /* glsl */ `
uniform sampler2D uMap;
varying vec2 vUv;
varying float vAlpha;
void main() {
  vec4 t = texture2D(uMap, vUv);
  gl_FragColor = vec4(t.rgb, t.a * vAlpha);
  if (gl_FragColor.a < 0.004) discard;
  #include <colorspace_fragment>
}`;

const MAX_POPS = 48;
/** §11 pop timing: spring in over 220 ms, hold to 700 ms, then a quick shrink-and-fade pop-out. */
export const POP = { growMs: 110, settleMs: 220, overshoot: 1.25, holdMs: 700, fadeMs: 180, outScale: 0.72, riseM: 0.6 } as const;
/**
 * Readability: at most this many world pops on screen; low-priority pops are dropped when crowded or
 * when a pop already sits within `nearM` that is younger than `nearSec`; a high-priority pop retires
 * the oldest low-priority ones instead.
 */
export const POP_LIMIT = { visible: 4, nearM: 3, nearSec: 0.35 } as const;
/** Pop priority: background chatter, normal, about the local bean / big moment. */
export const PopPriority = { LOW: 0, NORMAL: 1, HIGH: 2 } as const;
export type PopPriority = (typeof PopPriority)[keyof typeof PopPriority];
/** Screen-relative sizing: a 1× pop is this fraction of the screen height, clamped in px. */
/** The GAME! stamp: height and center as fractions of the screen height. */
const STAMP = { hFrac: 0.15, yFrac: 0.175 } as const;

const SIZE = {
  screenFrac: 0.072, minPx: 30, maxFrac: 0.13, replayMaxFrac: 0.085, refDepth: 22, edgePx: 10, hudTopPx: 64, hudTopFrac: 0.095, replayTopFrac: 0.22,
  /** Anchors beyond this NDC distance from the center get no word (live play / replays). */
  offNdc: 1.25, replayOffNdc: 1.02,
} as const;
/**
 * Screen-space de-overlap: pops are placed oldest / most important first and a later pop that would
 * cover one is lifted above it (or dropped below when there is no room under the HUD), with `padPx`
 * between words. The offset eases at `easeHz` so a pop slides aside instead of jumping.
 */
const DECLUTTER = { padPx: 4, easeHz: 18, passes: 6 } as const;
/**
 * The UI's nameplates (§12.4, a DOM pill whose bottom sits 0.35 m above each bean's 1.1 m body,
 * `PopAnchor.plateOf`) are obstacles too: pops never land on a name. Estimated size: height a fraction of the viewport, width a typical
 * "glyph + name + Dizzy %" pill. A head-anchored pop sits on top of its own bean's plate.
 */
const PLATE = { liftM: 0.35, hFrac: 0.025, minPx: 16, maxPx: 30, aspect: 6.5, beans: 8 } as const;

interface Pop {
  word: Word; x: number; y: number; z: number; scale: number; rot: number; age: number; life: number;
  /** Bean slot the pop follows (head), or -1 for a fixed world point. */
  slot: number;
  rise: number;
  priority: number;
  /** Last laid-out screen center (CSS px), for verification. */
  sx: number;
  sy: number;
  /** Smoothed de-overlap offset (CSS px) and whether it has been placed once. */
  offY: number;
  placed: boolean;
}

/** One frame's layout of a live pop (scratch, reused). */
interface Slot { p: Pop; cx: number; cy: number; w: number; h: number; bw: number; bh: number; alpha: number; y: number }
/** A fixed screen rectangle pops keep clear of (a nameplate), center x / y and box size. */
interface Block { cx: number; y: number; bw: number; bh: number }

export interface PopAnchor {
  headOf(slot: number, out: Vector3): boolean;
  /** The bottom-center of the UI nameplate of `slot` (three.js world), false when it shows none. */
  plateOf?(slot: number, out: Vector3): boolean;
}

/** Pop scale at age `ms`: 0 → 1.25 at 110 ms → 1.0 at 220 ms (§11), shrinking as it fades out. */
export function popScaleAt(ms: number): number {
  if (ms <= 0) return 0;
  if (ms > POP.holdMs) {
    const t = Math.min(1, (ms - POP.holdMs) / POP.fadeMs);
    return 1 + (POP.outScale - 1) * t * t;
  }
  if (ms < POP.growMs) {
    const t = ms / POP.growMs;
    return POP.overshoot * (1 - (1 - t) * (1 - t));
  }
  if (ms < POP.settleMs) {
    const t = (ms - POP.growMs) / (POP.settleMs - POP.growMs);
    return POP.overshoot + (1 - POP.overshoot) * (t * t * (3 - 2 * t));
  }
  return 1;
}

/** Pop alpha at age `ms`: opaque for 700 ms, then a quick fade (§11 "fades after 700 ms"). */
export function popAlphaAt(ms: number): number {
  if (ms <= POP.holdMs) return 1;
  const t = Math.min(1, (ms - POP.holdMs) / POP.fadeMs);
  return 1 - t * t;
}

export class TextPops {
  readonly scene = new Scene();
  readonly camera = new OrthographicCamera(0, 1, 0, 1, -10, 10);
  private readonly pops: Pop[] = [];
  private readonly free: Pop[] = [];
  private readonly geo = new InstancedBufferGeometry();
  private readonly aRect: InstancedBufferAttribute;
  private readonly aUv: InstancedBufferAttribute;
  private readonly aRotA: InstancedBufferAttribute;
  private readonly tex: Texture;
  private w = 1;
  private h = 1;
  /** Replays (close cameras): pops are capped smaller. */
  replay = false;
  private readonly v = new Vector3();
  private readonly head = new Vector3();
  private stamp: { word: Word; age: number; life: number } | null = null;
  private readonly slots: Slot[] = [];
  private readonly order: Slot[] = [];
  private readonly blocks: Block[] = Array.from({ length: PLATE.beans }, () => ({ cx: 0, y: 0, bw: 0, bh: 0 }));
  private blockCount = 0;
  /** The UI's real nameplate rects (`setPlateRects`), and how long ago (s) they were reported. */
  private readonly uiPlates = new Float32Array(PLATE.beans * 4);
  private uiPlateCount = 0;
  private uiPlateAge = Infinity;

  constructor() {
    this.tex = popAtlas();
    const quad = new PlaneGeometry(1, 1);
    this.geo.index = quad.index;
    this.geo.setAttribute('position', quad.getAttribute('position'));
    this.geo.setAttribute('uv', quad.getAttribute('uv'));
    const attr = (n: number): InstancedBufferAttribute => {
      const a = new InstancedBufferAttribute(new Float32Array((MAX_POPS + 1) * n), n);
      a.setUsage(DynamicDrawUsage);
      return a;
    };
    this.aRect = attr(4);
    this.aUv = attr(4);
    this.aRotA = attr(2);
    this.geo.setAttribute('iRect', this.aRect);
    this.geo.setAttribute('iUv', this.aUv);
    this.geo.setAttribute('iRotA', this.aRotA);
    this.geo.instanceCount = 0;
    const mat = new ShaderMaterial({
      uniforms: { uMap: { value: this.tex } },
      vertexShader: POP_VERTEX,
      fragmentShader: POP_FRAGMENT,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: DoubleSide,
    });
    const mesh = new Mesh(this.geo, mat);
    mesh.frustumCulled = false;
    mesh.name = 'textPops';
    this.scene.add(mesh);
    for (let i = 0; i < MAX_POPS; i++) {
      this.free.push({ word: Word.BONK, x: 0, y: 0, z: 0, scale: 1, rot: 0, age: 0, life: 1, slot: -1, rise: POP.riseM, priority: 1, sx: 0, sy: 0, offY: 0, placed: false });
      this.slots.push({ p: this.free[i], cx: 0, cy: 0, w: 0, h: 0, bw: 0, bh: 0, alpha: 0, y: 0 });
    }
  }

  get count(): number {
    return this.pops.length;
  }

  /** Words spawned since the last `clear` (verification). */
  readonly log: Word[] = [];

  /**
   * A pop at sim (x, y) and height z (meters). `slot` ≥ 0 makes it follow that bean's head; `life`
   * (seconds) replaces the default 700 ms hold + fade (item icons show for 0.8 s). Returns false when
   * a low-priority pop is dropped to keep the screen readable.
   */
  spawn(word: Word, x: number, y: number, z: number, scale = 1, slot = -1, rise: number = POP.riseM, life = 0,
    priority: PopPriority = PopPriority.NORMAL): boolean {
    let visible = 0;
    let near = false;
    for (const q of this.pops) {
      if (q.age * 1000 >= POP.holdMs) continue;
      visible++;
      if (q.age < POP_LIMIT.nearSec && Math.hypot(q.x - x, q.y - y) < POP_LIMIT.nearM) near = true;
    }
    if (priority === PopPriority.LOW && (near || visible >= POP_LIMIT.visible - 1)) return false;
    if (priority === PopPriority.NORMAL && visible >= POP_LIMIT.visible) return false;
    if (priority === PopPriority.HIGH && visible >= POP_LIMIT.visible) this.retireOldest();
    let p = this.free.pop();
    if (!p) {
      p = this.pops.shift();
      if (!p) return false;
    }
    p.word = word;
    p.x = x;
    p.y = y;
    p.z = z;
    p.scale = scale;
    p.rot = (Math.random() - 0.5) * 0.28;
    p.age = 0;
    p.life = life > 0 ? life : (POP.holdMs + POP.fadeMs) / 1000;
    p.slot = slot;
    p.rise = rise;
    p.priority = priority;
    p.offY = 0;
    p.placed = false;
    // Words never overlap on screen: `declutter` lifts a newer word above any word it would cover
    // (a YEET! right after a YOINK! goes on top of it, not over it).
    this.pops.push(p);
    if (this.log.length < 256) this.log.push(word);
    return true;
  }

  /** Sends the oldest non-high pop into its pop-out. */
  private retireOldest(): void {
    for (const q of this.pops) {
      if (q.priority >= PopPriority.HIGH || q.age * 1000 >= POP.holdMs) continue;
      q.age = Math.max(q.age, POP.holdMs / 1000);
      q.life = Math.min(q.life, (POP.holdMs + POP.fadeMs) / 1000);
      return;
    }
  }

  /** The full-screen stamp (GAME!), held for `seconds`; live pops pop out so it owns the screen. */
  showStamp(word: Word, seconds: number): void {
    this.stamp = { word, age: 0, life: seconds };
    for (const q of this.pops) {
      q.age = Math.max(q.age, POP.holdMs / 1000);
      q.life = Math.min(q.life, (POP.holdMs + POP.fadeMs) / 1000);
    }
    if (this.log.length < 256) this.log.push(word);
  }

  /** Live pops (verification). Allocates. */
  snapshot(): { word: number; x: number; y: number; z: number; slot: number; age: number; priority: number; sx: number; sy: number }[] {
    return this.pops.map((p) => ({ word: p.word, x: p.x, y: p.y, z: p.z, slot: p.slot, age: p.age, priority: p.priority, sx: p.sx, sy: p.sy }));
  }

  /**
   * The UI's laid-out nameplates as flat [centerX, bottomY, width, height] CSS px per plate (after
   * its own de-overlap); used instead of the estimate while fresh (≤ 0.25 s). null clears them.
   */
  setPlateRects(rects: ArrayLike<number> | null): void {
    if (!rects) {
      this.uiPlateCount = 0;
      this.uiPlateAge = Infinity;
      return;
    }
    const n = Math.min(PLATE.beans, Math.floor(rects.length / 4));
    for (let i = 0; i < n * 4; i++) this.uiPlates[i] = rects[i];
    this.uiPlateCount = n;
    this.uiPlateAge = 0;
  }

  /** The nameplate obstacles of the last layout, as [centerX, centerY, w, h] CSS px (verification). */
  plateBlocks(): number[][] {
    return this.blocks.slice(0, this.blockCount).map((b) => [Math.round(b.cx), Math.round(b.y), Math.round(b.bw), Math.round(b.bh)]);
  }

  get stampWord(): Word | null {
    return this.stamp ? this.stamp.word : null;
  }

  clear(): void {
    while (this.pops.length > 0) this.free.push(this.pops.pop() as Pop);
    this.stamp = null;
    this.geo.instanceCount = 0;
    this.log.length = 0;
  }

  resize(w: number, h: number): void {
    this.w = Math.max(1, w);
    this.h = Math.max(1, h);
    this.camera.left = 0;
    this.camera.right = this.w;
    this.camera.top = 0;
    this.camera.bottom = this.h;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Advances pops by `dt` (game seconds; slow motion slows them) and the stamp by `realDt`, then
   * lays them out on screen through `cam`.
   */
  update(dt: number, realDt: number, cam: PerspectiveCamera, anchors: PopAnchor): void {
    const rect = this.aRect.array as Float32Array;
    const uvs = this.aUv.array as Float32Array;
    const ra = this.aRotA.array as Float32Array;
    // Clear of the HUD's top bar (round pill, timer) and the screen edges; in replays also clear of
    // the VHS camcorder tag under it.
    const hudTop = Math.max(SIZE.hudTopPx, this.h * SIZE.hudTopFrac);
    const top = this.replay ? Math.max(hudTop, this.h * SIZE.replayTopFrac) : hudTop;
    const plateH = Math.min(PLATE.maxPx, Math.max(PLATE.minPx, this.h * PLATE.hFrac));
    // Nameplates as fixed obstacles: the UI's real rects while it reports them, else estimated from
    // the anchors (in replays only the focus bean's shows; `plateOf` knows).
    this.blockCount = 0;
    this.uiPlateAge += realDt;
    if (this.pops.length > 0 && this.uiPlateAge <= 0.25) {
      for (let i = 0; i < this.uiPlateCount; i++) {
        const b = this.blocks[this.blockCount++];
        const o = i * 4;
        b.cx = this.uiPlates[o];
        b.bw = this.uiPlates[o + 2];
        b.bh = this.uiPlates[o + 3];
        b.y = this.uiPlates[o + 1] - b.bh * 0.5;
      }
    } else if (this.pops.length > 0 && anchors.plateOf) {
      for (let slot = 0; slot < PLATE.beans; slot++) {
        if (!anchors.plateOf(slot, this.head)) continue;
        this.v.copy(this.head).project(cam);
        if (this.v.z > 1 || this.v.z < -1) continue;
        const b = this.blocks[this.blockCount++];
        b.cx = ((this.v.x + 1) / 2) * this.w;
        b.bh = plateH;
        b.bw = plateH * PLATE.aspect;
        b.y = ((1 - this.v.y) / 2) * this.h - plateH * 0.5;
      }
    }
    let m = 0;
    for (let i = 0; i < this.pops.length; ) {
      const p = this.pops[i];
      p.age += dt;
      if (p.age >= p.life) {
        this.pops.splice(i, 1);
        this.free.push(p);
        continue;
      }
      i++;
      const ms = p.age * 1000;
      const k = popScaleAt(ms) * p.scale;
      const lift = p.rise * Math.min(1, p.age / p.life) * (2 - Math.min(1, p.age / p.life));
      let onPlate = false;
      if (p.slot >= 0 && anchors.headOf(p.slot, this.head)) {
        this.v.set(this.head.x, this.head.y + PLATE.liftM + lift, this.head.z);
        onPlate = !this.replay;
      } else {
        this.v.set(p.x, p.z + lift, p.y);
      }
      // Depth for distance-aware sizing, then projection to CSS px.
      const depth = this.v.distanceTo(cam.position);
      this.v.project(cam);
      if (this.v.z > 1 || this.v.z < -1) continue;
      // Words for points well off screen are dropped, not pinned to the edge (in a replay's close
      // camera, a CLUTCH! across the arena would otherwise sit on the frame's border).
      const off = this.replay ? SIZE.replayOffNdc : SIZE.offNdc;
      if (Math.abs(this.v.x) > off || Math.abs(this.v.y) > off) continue;
      const sx = ((this.v.x + 1) / 2) * this.w;
      const sy = ((1 - this.v.y) / 2) * this.h;
      const hPx = this.wordHeightPx(p.word, k, depth);
      const aspect = wordAspect(p.word);
      // Bottom-anchored on the point (words sit above what they describe, never over it), and kept
      // fully on screen. Overlaps are solved below on the settled (not springing) size.
      const settled = this.wordHeightPx(p.word, p.scale, depth);
      const sl = this.slots[m++];
      sl.p = p;
      sl.h = hPx;
      sl.w = hPx * aspect;
      sl.bh = settled * 0.82;
      sl.bw = settled * aspect * 0.9;
      sl.alpha = popAlphaAt(ms);
      const mx = sl.w * 0.5 + SIZE.edgePx;
      sl.cx = Math.min(this.w - mx, Math.max(mx, sx));
      sl.cy = sy - settled * 0.5 - (onPlate ? plateH : 0);
    }
    this.declutter(m, top, realDt);
    let n = 0;
    for (let i = 0; i < m; i++) {
      const sl = this.slots[i];
      const p = sl.p;
      const my = sl.h * 0.5 + SIZE.edgePx;
      const cy = Math.min(this.h - my, Math.max(my + top, sl.cy + p.offY));
      p.sx = sl.cx;
      p.sy = cy;
      this.writeQuad(n, rect, uvs, ra, p.word, sl.cx, cy, sl.w, sl.h, p.rot, sl.alpha);
      n++;
    }
    const st = this.stamp;
    if (st) {
      st.age += realDt;
      if (st.age >= st.life) {
        this.stamp = null;
      } else {
        const ms = st.age * 1000;
        // Slam: 2.2× → 1.0 with a punchy overshoot, slight tilt, fade at the end.
        const t = Math.min(1, ms / 180);
        const slam = 2.2 - 1.2 * t + (t >= 1 ? 0.08 * Math.sin(Math.min(1, (ms - 180) / 260) * Math.PI) * (1 - Math.min(1, (ms - 180) / 260)) : 0);
        const alpha = Math.min(1, ms / 60) * Math.min(1, (st.life - st.age) / 0.35);
        // Top band: just below the HUD's top bar (~7%), clear of the band around 33% where the UI
        // slides its ROUND OVER / "1st: …" banner in.
        const hPx = Math.min(this.h * STAMP.hFrac, (this.w * 0.8) / Math.max(1, wordAspect(st.word))) * slam;
        this.writeQuad(n, rect, uvs, ra, st.word, this.w / 2, this.h * STAMP.yFrac, hPx * wordAspect(st.word), hPx, -0.1, alpha);
        n++;
      }
    }
    this.geo.instanceCount = n;
    if (n > 0) {
      this.aRect.needsUpdate = true;
      this.aUv.needsUpdate = true;
      this.aRotA.needsUpdate = true;
    }
  }

  /**
   * Screen-space de-overlap of the `m` laid-out pops: placed by priority, then age (the older word
   * keeps its spot); a pop that would cover a placed one moves just above it, or below it when the
   * HUD leaves no room above. The wanted offset is eased per pop so words slide apart smoothly.
   */
  private declutter(m: number, top: number, realDt: number): void {
    const order = this.order;
    order.length = 0;
    // Insertion sort (a handful of pops; no comparator closure or temp arrays per frame).
    for (let i = 0; i < m; i++) {
      const sl = this.slots[i];
      let j = order.length;
      order.push(sl);
      while (j > 0 && (order[j - 1].p.priority < sl.p.priority || (order[j - 1].p.priority === sl.p.priority && order[j - 1].p.age < sl.p.age))) {
        order[j] = order[j - 1];
        j--;
      }
      order[j] = sl;
    }
    const pad = DECLUTTER.padPx;
    for (let i = 0; i < order.length; i++) {
      const a = order[i];
      let y = a.cy;
      for (let pass = 0; pass < DECLUTTER.passes; pass++) {
        let moved = false;
        const n = i + this.blockCount;
        for (let j = 0; j < n; j++) {
          const b: Block = j < this.blockCount ? this.blocks[j] : order[j - this.blockCount];
          if (Math.abs(a.cx - b.cx) * 2 >= a.bw + b.bw + pad * 2) continue;
          if (Math.abs(y - b.y) * 2 >= a.bh + b.bh + pad * 2) continue;
          const above = b.y - (b.bh + a.bh) * 0.5 - pad;
          y = above - a.h * 0.5 >= top ? above : b.y + (b.bh + a.bh) * 0.5 + pad;
          moved = true;
        }
        if (!moved) break;
      }
      a.y = y;
      const want = y - a.cy;
      const p = a.p;
      if (!p.placed) {
        p.offY = want;
        p.placed = true;
      } else {
        p.offY += (want - p.offY) * (1 - Math.exp(-DECLUTTER.easeHz * Math.max(0, realDt)));
      }
    }
  }

  private wordHeightPx(word: Word, k: number, depth: number): number {
    // Sized by the smaller screen side (portrait phones are narrow, landscape ones short).
    const side = Math.min(this.h, this.w * 0.75);
    const base = side * SIZE.screenFrac * Math.sqrt(SIZE.refDepth / Math.max(4, depth));
    const icon = word >= Word.ICON_BANANA ? 1.15 : 1;
    const px = Math.max(SIZE.minPx, base) * icon;
    return Math.min(side * (this.replay ? SIZE.replayMaxFrac : SIZE.maxFrac), px) * k;
  }

  private writeQuad(i: number, rect: Float32Array, uvs: Float32Array, ra: Float32Array, word: Word,
    cx: number, cy: number, w: number, h: number, rot: number, alpha: number): void {
    if (i > MAX_POPS) return;
    const r = word * 4;
    rect[i * 4] = cx;
    rect[i * 4 + 1] = cy;
    rect[i * 4 + 2] = w;
    // The overlay camera's y axis points down, so flip the quad vertically.
    rect[i * 4 + 3] = -h;
    uvs[i * 4] = rects[r] / ATLAS_W;
    uvs[i * 4 + 1] = 1 - (rects[r + 1] + rects[r + 3]) / ATLAS_H;
    uvs[i * 4 + 2] = rects[r + 2] / ATLAS_W;
    uvs[i * 4 + 3] = rects[r + 3] / ATLAS_H;
    ra[i * 2] = rot;
    ra[i * 2 + 1] = alpha;
  }
}
